/**
 * VulnerabilitySearchFilters Component
 *
 * Main filter toolbar for the vulnerabilities table.
 * Contains all filter dropdowns consolidated into a single reusable component.
 *
 * Note: This component returns just the filter content (no wrapper).
 * Parent component is responsible for layout via TableControlsContainer.
 *
 * This component was extracted from index.tsx during Phase 5 refactoring
 * to reduce the main component size and improve maintainability.
 */

import React, { useMemo } from 'react';

import { DateRangeFilterDropdown } from '@/components/filters/DateRangeFilterDropdown';
import { FilterDropdown } from '@/components/filters/FilterDropdown';
import type { FilterOption } from '@/components/filters/types';
import { ClearFiltersButton } from '@/components/legacy-ui/ClearFiltersButton';
import { useHasActiveFilters } from '@/hooks/useHasActiveFilters';
import { RangeSelectorDropdown } from '@/sections/vulnerabilities/components/SelectRange';
import { getFilterDropdownWidth } from '@/sections/vulnerabilities/config';
import {
  BOOLEAN_FILTERS,
  LAST_SEEN_FILTERS,
} from '@/sections/vulnerabilities/constants/filters';
import { UseVulnerabilityFiltersReturn } from '@/sections/vulnerabilities/hooks/useVulnerabilityFilters';
import { GroupOption } from '@/sections/vulnerabilities/utils/sourceDisplay.util';
import { useEntityFilters } from '@/utils/entityFilters.util';

/**
 * Component props
 */
export interface VulnerabilitySearchFiltersProps {
  /** Filter state and handlers from useVulnerabilityFilters hook */
  filterHandlers: UseVulnerabilityFiltersReturn;
  /** Whether current user is Praetorian user (for source filter) */
  isPraetorianUser?: boolean;
  /** Whether current org is demo org */
  isDemoOrg?: boolean;
  /** When provided, only show filter dropdowns for these keys (e.g., ['status', 'severity', 'origin']) */
  visibleFilters?: string[];
}

/**
 * VulnerabilitySearchFilters component renders the complete filter toolbar
 * including search bar and all advanced filter dropdowns.
 *
 * Filter options are fetched internally via useEntityFilters hook, providing
 * automatic alphabetical ordering of filters.
 *
 * @param props - Component props
 * @returns Filter toolbar JSX
 *
 * @example
 * ```tsx
 * const filterHandlers = useVulnerabilityFilters({ filters, setFilters });
 *
 * <VulnerabilitySearchFilters
 *   filterHandlers={filterHandlers}
 *   isPraetorianUser={isPraetorianUser}
 *   isDemoOrg={isDemoOrg}
 * />
 * ```
 */
export const VulnerabilitySearchFilters: React.FC<
  VulnerabilitySearchFiltersProps
> = ({
  filterHandlers,
  isPraetorianUser = false,
  isDemoOrg = false,
  visibleFilters,
}) => {
  // Destructure filter handlers for cleaner JSX
  // Note: handleSearchChange moved to parent component (AppRouteBody headerAction)
  const {
    filters,
    setFilters,
    handleSeverityFilter,
    handleStatusFilter,
    handleAttackSurfaceFilter,
    handleTagFilter,
    handleFrameworkFilter,
    handleCVSSFilter,
    handleEPSSFilter,
    handleDateRangeFilter,
    handleLastSeenFilter,
    handleKEVFilter,
    handleSSOFilter,
    handleTicketFilter,
    handleOriginFilter,
    clearAllFilters,
    getSelectedValues,
  } = filterHandlers;

  // Use centralized hook for consistent filter detection (includes search + dropdown filters)
  const hasActiveFilters = useHasActiveFilters(filters);

  // Get entity filters with alphabetical ordering
  const {
    filterOptions: entityFilterOptions,
    sortedOptions,
    status: filterOptionsStatus,
  } = useEntityFilters('Vulnerability', 'vuln-table');

  // Range colors for CVSS/EPSS sliders
  const rangeColors = useMemo(
    () => ({
      40: 'bg-chariot-stroke-info text-chariot-status-info',
      70: 'bg-chariot-stroke-medium text-chariot-status-medium',
      90: 'bg-chariot-stroke-high text-chariot-status-high',
      100: 'bg-chariot-stroke-critical text-chariot-status-critical',
    }),
    []
  );

  // Map filter keys to their respective handlers
  const getFilterHandler = (
    key: string
  ): ((
    selectedOptions: Array<{ value?: string | number; label?: string }>
  ) => void) => {
    switch (key) {
      case 'severity':
        return handleSeverityFilter;
      case 'status':
        return handleStatusFilter;
      case 'attackSurface':
        return handleAttackSurfaceFilter;
      case 'tags':
        return handleTagFilter;
      case 'frameworks':
        return handleFrameworkFilter;
      case 'kev':
        return handleKEVFilter;
      case 'sso':
        return handleSSOFilter;
      case 'origin':
        return handleOriginFilter;
      default:
        return () => {};
    }
  };

  // Determine if a filter should show "Select All" checkbox
  const shouldShowSelectAll = (key: string): boolean => {
    // Boolean filters (kev, sso) don't show "Select All"
    return !['kev', 'sso'].includes(key);
  };

  return (
    <>
      {/* Dynamic Filters - Alphabetically Ordered */}
      {sortedOptions.map((option, index) => {
        const key = option.value as string;

        // Skip range filters (CVSS, EPSS) and SSO - they're handled separately below
        if (key === 'cvss' || key === 'epss' || key === 'sso') {
          return null;
        }

        // When visibleFilters is provided, only show filters in that list
        if (visibleFilters && !visibleFilters.includes(key)) {
          return null;
        }

        // Get filter options from entityFilterOptions
        const filterOption =
          entityFilterOptions &&
          typeof entityFilterOptions === 'object' &&
          !Array.isArray(entityFilterOptions)
            ? (entityFilterOptions as Record<string, unknown>)[key]
            : null;

        if (!filterOption) {
          return null;
        }

        // Get appropriate handler for this filter
        const handler = getFilterHandler(key);

        // Determine selected values key (handle special case for 'tags')
        const selectedValuesKey = key === 'tag' ? 'tags' : key;

        return (
          <FilterDropdown
            key={index}
            id={key}
            isLoading={filterOptionsStatus === 'pending'}
            label={option.label}
            options={filterOption as (FilterOption | GroupOption)[]}
            selectedValues={getSelectedValues(selectedValuesKey)}
            onApply={handler}
            onClear={() => {
              handler([]);
              setFilters(prev => ({
                ...prev,
                negatedFilterKeys: prev.negatedFilterKeys?.filter(
                  k => k !== key
                ),
              }));
            }}
            showSelectAll={shouldShowSelectAll(key)}
            isPraetorianUser={
              key === 'origin' ? isPraetorianUser && !isDemoOrg : undefined
            }
            width={getFilterDropdownWidth(key)}
            isNegated={filters.negatedFilterKeys?.includes(key) ?? false}
            onToggleNegation={() => {
              setFilters(prev => ({
                ...prev,
                negatedFilterKeys: prev.negatedFilterKeys?.includes(key)
                  ? prev.negatedFilterKeys.filter(k => k !== key)
                  : [...(prev.negatedFilterKeys ?? []), key],
              }));
            }}
          />
        );
      })}

      {/* SSO Enabled Filter - Static options (conditionally rendered based on visibleFilters) */}
      {(!visibleFilters || visibleFilters.includes('sso')) && (
        <FilterDropdown
          id="sso"
          isLoading={false}
          label="SSO"
          options={BOOLEAN_FILTERS}
          selectedValues={getSelectedValues('sso')}
          onApply={handleSSOFilter}
          onClear={() => {
            handleSSOFilter([]);
            setFilters(prev => ({
              ...prev,
              negatedFilterKeys: prev.negatedFilterKeys?.filter(
                k => k !== 'sso'
              ),
            }));
          }}
          showSelectAll={false}
          width={getFilterDropdownWidth('sso')}
          isNegated={filters.negatedFilterKeys?.includes('sso') ?? false}
          onToggleNegation={() => {
            setFilters(prev => ({
              ...prev,
              negatedFilterKeys: prev.negatedFilterKeys?.includes('sso')
                ? prev.negatedFilterKeys.filter(k => k !== 'sso')
                : [...(prev.negatedFilterKeys ?? []), 'sso'],
            }));
          }}
        />
      )}

      {/* Ticket Filter - Static options (conditionally rendered based on visibleFilters) */}
      {(!visibleFilters || visibleFilters.includes('ticket')) && (
        <FilterDropdown
          id="ticket"
          isLoading={false}
          label="Ticket"
          options={[
            { label: 'Has Ticket', value: 'has-ticket', id: 'has-ticket' },
            { label: 'No Ticket', value: 'no-ticket', id: 'no-ticket' },
          ]}
          selectedValues={getSelectedValues('ticket')}
          onApply={handleTicketFilter}
          onClear={() => {
            handleTicketFilter([]);
            setFilters(prev => ({
              ...prev,
              negatedFilterKeys: prev.negatedFilterKeys?.filter(
                k => k !== 'ticket'
              ),
            }));
          }}
          showSelectAll={false}
          width={getFilterDropdownWidth('ticket')}
          isNegated={filters.negatedFilterKeys?.includes('ticket') ?? false}
          onToggleNegation={() => {
            setFilters(prev => ({
              ...prev,
              negatedFilterKeys: prev.negatedFilterKeys?.includes('ticket')
                ? prev.negatedFilterKeys.filter(k => k !== 'ticket')
                : [...(prev.negatedFilterKeys ?? []), 'ticket'],
            }));
          }}
        />
      )}

      {/* CVSS Range Filter (conditionally rendered based on visibleFilters) */}
      {(!visibleFilters || visibleFilters.includes('cvss')) && (
        <RangeSelectorDropdown
          label="cvss"
          value={filters.advancedFilters?.cvss}
          step={1}
          maxRange={10}
          hideSelectedRange
          rangeColors={rangeColors}
          onApply={handleCVSSFilter}
        />
      )}

      {/* EPSS Range Filter (conditionally rendered based on visibleFilters) */}
      {(!visibleFilters || visibleFilters.includes('epss')) && (
        <RangeSelectorDropdown
          hideSelectedRange
          rangeColors={rangeColors}
          label="epss"
          value={filters.advancedFilters?.epss}
          step={0.01}
          maxRange={1}
          onApply={handleEPSSFilter}
        />
      )}

      {/* Last Seen Filter (conditionally rendered based on visibleFilters) */}
      {(!visibleFilters || visibleFilters.includes('lastSeen')) && (
        <FilterDropdown
          id="lastSeen"
          isLoading={false}
          label="Last Seen"
          options={LAST_SEEN_FILTERS}
          selectedValues={getSelectedValues('lastSeen')}
          onApply={handleLastSeenFilter}
          onClear={() => {
            handleLastSeenFilter([]);
            setFilters(prev => ({
              ...prev,
              negatedFilterKeys: prev.negatedFilterKeys?.filter(
                k => k !== 'lastSeen'
              ),
            }));
          }}
          showSelectAll={false}
          maxSelections={1}
          width={getFilterDropdownWidth('lastSeen')}
          isNegated={filters.negatedFilterKeys?.includes('lastSeen') ?? false}
          onToggleNegation={() => {
            setFilters(prev => ({
              ...prev,
              negatedFilterKeys: prev.negatedFilterKeys?.includes('lastSeen')
                ? prev.negatedFilterKeys.filter(k => k !== 'lastSeen')
                : [...(prev.negatedFilterKeys ?? []), 'lastSeen'],
            }));
          }}
        />
      )}

      {/* Date Range Filter (conditionally rendered based on visibleFilters) */}
      {(!visibleFilters || visibleFilters.includes('dateRange')) && (
        <DateRangeFilterDropdown
          label="Date Range"
          value={filters.advancedFilters?.dateRange}
          onApply={handleDateRangeFilter}
        />
      )}

      {/* Clear Filters Button */}
      {hasActiveFilters && <ClearFiltersButton onClick={clearAllFilters} />}
    </>
  );
};
